import { useState, useCallback, useEffect } from "react";
import { Link, useRouteMatch, useLocation, useHistory } from "react-router-dom";
import { useT, T } from "@transifex/react";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import ChevronLeftSharpIcon from "@material-ui/icons/ChevronLeftSharp";
import { AccountIcon } from "./account-icon";
import { usePatientAuth } from "context/patient-auth";
import { useScrollbar } from "context/scrollbar";
import { Button } from "storybook/components/button";
import { LanguagePicker } from "components/language-picker";
import {
  Container,
  NavContent,
  AccountText,
  NavMenuItem,
  HomeMenuItem,
  NavSectionLeft,
  MenuButtonIcon,
  NavMenuItemLink,
  MenuContainer,
  HeaderText,
  RightContainer,
  LanguageContainer,
} from "./styles";

type Routes = {
  [key: string]: {
    title: string;
    backTo?: string;
  };
};

const getRoutesMap = (t: any): Routes => ({
  "/settings": {
    title: t("Account Settings"),
  },
  "/settings/personal-information": {
    title: t("Personal Information"),
  },
  "/settings/contact-information": {
    title: t("Contact Information"),
  },
  "/settings/consent": {
    title: t("Data Consent"),
  },
  "/reports/:reportId": {
    title: t("Results"),
  },
  "/complete/take-action": {
    title: t("Your Next Steps"),
    backTo: "/complete",
  },
});

export function Header() {
  const t = useT();
  const { logout } = usePatientAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const { scrollbarVisible, scrollbarWidth } = useScrollbar();

  const location = useLocation();
  const isReportPage = useRouteMatch("/reports/:reportId");
  const isSettingsPage = useRouteMatch("/settings");
  const isTakeActionPage = useRouteMatch("/complete/take-action");
  const history = useHistory();

  const isBackNav = isReportPage || isSettingsPage || isTakeActionPage;

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLogout = useCallback(() => {
    handleClose();
    logout();
  }, [handleClose, logout]);

  const handleBackClick = useCallback(() => {
    const backTo = getRoutesMap(t)[location.pathname]?.backTo;

    if (backTo) {
      history.push(backTo);
    } else {
      history.goBack();
    }
  }, [history, location.pathname, t]);

  useEffect(() => {
    if (isBackNav) {
      setAnchorEl(null);
    }
  }, [isBackNav]);

  if (isBackNav) {
    return (
      <Container
        $scrollbarWidth={scrollbarWidth}
        $scrollbarVisible={scrollbarVisible}
        $isBackNav={Boolean(isBackNav)}
      >
        <NavContent>
          <Button
            theme="default"
            variant="text"
            color="default"
            startIcon={<ChevronLeftSharpIcon />}
            onClick={handleBackClick}
          />
          <HeaderText variant="h6" component="h1" color="ciqBrandDark">
            <b>
              {isReportPage
                ? t("Results")
                : getRoutesMap(t)[location.pathname]?.title}
            </b>
          </HeaderText>
          <LanguageContainer>
            <LanguagePicker />
          </LanguageContainer>
        </NavContent>
      </Container>
    );
  }

  return (
    <Container
      $scrollbarWidth={scrollbarWidth}
      $scrollbarVisible={scrollbarVisible}
    >
      <NavContent>
        <NavSectionLeft>
          <Link to="/">
            <img
              alt={t("CancerIQ patient logo")}
              src="/ciq-logo.png"
              width="135"
            />
          </Link>
        </NavSectionLeft>
        <RightContainer>
          <LanguagePicker />
          <MenuContainer>
            <MenuButtonIcon
              onClick={handleClick}
              disableFocusRipple={true}
              disableRipple={true}
            >
              <AccountIcon />
              <AccountText>
                <T _str="Account" />
              </AccountText>
            </MenuButtonIcon>
            <Menu
              id="simple-menu"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <HomeMenuItem className="home">
                <NavMenuItemLink to="/self-assessment">
                  <T _str="Home" />
                </NavMenuItemLink>
              </HomeMenuItem>
              <MenuItem>
                <NavMenuItemLink to="/settings">
                  <T _str="Edit Account" />
                </NavMenuItemLink>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <NavMenuItem>
                  <ExitToAppIcon />
                  <T _str="Log out" />
                </NavMenuItem>
              </MenuItem>
            </Menu>
          </MenuContainer>
        </RightContainer>
      </NavContent>
    </Container>
  );
}
